<template>
  <base-page-template>
    <template #toolbar-items>
      <v-toolbar-title class="with-divider">
        データ実装管理
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        @click="dialogOmakase.visible = true;"
        color="primary"
      >
        <v-icon class="mr-2">mdi-checkbox-marked-circle-auto-outline</v-icon>
        辞書からおまかせ実装
      </v-btn>
    </template>

    <owner-info
        :account="account">
    </owner-info>

    <tab-item-template>
      <template #toolbar-items>
        <el-tooltip content="新規" placement="bottom" v-if="isEditable">
          <v-btn icon @click="openCreateDialog">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </el-tooltip>
        <v-spacer></v-spacer>
        <toolbar-inline>
          <model-select
              v-model="controllerId"
              :items="controllers"
              placeholder="コントローラ"
              clearable>
          </model-select>
        </toolbar-inline>
      </template>

      <data-implementation-table
          :dataImplementations="dataImplementationsView"
          @openDeleteDialog="openDeleteDialog"
          @update="createDataImplementation($event)">
      </data-implementation-table>
    </tab-item-template>

    <!-- 作成ダイアログ -->
    <simple-dialog
        :value="dialogCreate.visible"
        title-text="データ実装作成"
        :header-text="dialogCreate.controllerId ?
          '追加・更新するデータ構成モデルを選択してください' :
          '追加・更新の対象となるコントローラを選択してください'
        "
        @close="closeCreateDialog"
        no-padding>
      <template #apply-button="{applyText, modifierKeys}">
        {{ (() => {
        const prefix = modifierKeys.alt ? '続けて' : '';
        switch(dialogCreate.updateType) {
          case 'add': return `${prefix}追加`;
          case 'update': return `${prefix}更新`;
          default: return applyText;
        }
      })() }}
      </template>
      <template #header="{headerText}">
        <h3>{{ headerText }}</h3>
        <div class="mt-1">alt キーを押しながら追加・更新ボタンを押すと、続けて操作する事ができます。</div>
      </template>
      <template #default>
        <v-container>
          <model-select
              v-model="dialogCreate.controllerId"
              :items="controllers"
              label="コントローラ"
              :rules="[v => !!v || 'コントローラが必要です']">
          </model-select>
        </v-container>
        <v-divider></v-divider>
        <data-component-model-table
            v-model="dialogCreate.dataComponentModelId"
            :style="dialogCreate.controllerId ? {} : {
              filter: 'brightness(0.9) opacity(0.5)',
              pointerEvents: 'none'
            }"
            :selected-controller-id="dialogCreate.controllerId"
            :existing-data-implementations="dataImplementations"
            height="500"
            @select="dialogCreate.updateType = ($event ? $event.updateType : null)">
        </data-component-model-table>
      </template>
    </simple-dialog>

    <!-- 削除ダイアログ -->
    <simple-dialog
        v-model="dialogDelete.visible"
        title-text="データ実装削除"
        title-color="delete"
        apply-text="削除"
        header-text="以下のデータ実装を削除します。本当によろしいですか？"
        @close="closeDeleteDialog"
        @apply="deleteDataImplementation">
      <data-implementation-delete-form
        v-model="dialogDelete.form">
      </data-implementation-delete-form>
    </simple-dialog>

    <!-- 辞書からおまかせ実装ダイアログ -->
    <omakase-implementation-dialog
      v-model="dialogOmakase.visible"
      :initial-controller-id="controllerId"
      @done:save="loadDataImplementations"
    />
  </base-page-template>
</template>

<script>
import {mapState, mapActions, mapGetters, mapMutations} from "vuex";
import PathInfoMixin from "@/router/PathInfoMixin";
import IoablePageMixin from "@/router/IoablePageMixin";
import BasePageTemplate from "@/components/templates/BasePageTemplate.vue";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import DataImplementationTable from "@/components/pages/implementation/DataImplementationList/DataImplementationTable.vue";
import DataComponentModelTable from "@/components/pages/implementation/DataImplementationList/DataComponentModelTable.vue";
import DataImplementationDeleteForm from "@/components/pages/implementation/DataImplementationList/DataImplementationDeleteForm.vue";
import OwnerInfo from "@/components/organisms/OwnerInfo.vue";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import ModelSelect from "@/components/molecules/ModelSelect.vue";
import ToolbarInline from "@/components/atoms/ToolbarInline.vue";
import OmakaseImplementationDialog
  from "@/components/pages/implementation/ControllerShow/OmakaseImplementationDialog.vue";

export default {
  name: 'DataImplementationList',
  components: {
    OmakaseImplementationDialog,
    ToolbarInline,
    ModelSelect,
    TabItemTemplate,
    BasePageTemplate,
    SimpleDialog,
    DataImplementationTable,
    DataComponentModelTable,
    DataImplementationDeleteForm,
    OwnerInfo
  },
  mixins: [
    PathInfoMixin,
    IoablePageMixin
  ],
  data() {
    return {
      dialogCreate: {
        visible: false,
        controllerId: null,
        dataComponentModelId: null,
        updateType: null
      },
      dialogDelete: {
        visible: false,
        form: {}
      },
      dialogOmakase: {
        visible: false,
      },
      controllerId: null
    }
  },
  created() {
    if (this.$route.params.controllerId) {
      this.controllerId = this.$route.params.controllerId
    }
  },
  computed: {
    ...mapState('hcs/dataImplementation', ['dataImplementations']),
    ...mapState('hcs/controller', ['controllers']),
    ...mapGetters({
      account: 'hcs/account',
    }),
    dataImplementationsView() {
      if(this.controllerId) {
        return this.dataImplementations.filter(di => di.controllerId === this.controllerId);
      } else {
        return this.dataImplementations.slice();
      }
    },
  },
  methods: {
    ...mapActions({
      _createDataImplementation: 'hcs/dataImplementation/create',
    }),
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    async load() {
      await this.loadDataImplementations();
      await this.$store.dispatch('hcs/controller/findList', {
        q: { terminalIdEq: this.account.user?.terminal?.id }
      });
    },
    /**
     * データ実装を取得
     *
     * @return {Promise<DataImplementation[]>}
     */
    async loadDataImplementations() {
      await this.$store.dispatch('hcs/dataImplementation/findList');
    },
    openCreateDialog() {
      this.dialogCreate = {
        visible: true,
        controllerId: this.controllerId,
        dataComponentModelId: null,
        updateType: null
      };
    },
    closeCreateDialog(apply, {modifierKeys, enableApply}) {
      if(apply) {
        const controllerId = this.dialogCreate.controllerId;
        const dataComponentModelId = this.dialogCreate.dataComponentModelId;
        const updateType = this.dialogCreate.updateType;
        this.createDataImplementation({
          dataComponentModelId,
          controllerId,
          updateType
        }).finally(() => {
          enableApply();
        });
        if(modifierKeys.alt) {
          return; // ダイアログを閉じない
        }
      }
      this.dialogCreate.visible = false;
    },
    openDeleteDialog(dataImplementation) {
      this.dialogDelete = {
        visible: true,
        form: dataImplementation,
      }
    },
    closeDeleteDialog() {
      this.dialogDelete.visible = false;
    },
    async deleteDataImplementation() {
      try {
        await this.$store.dispatch('hcs/dataImplementation/delete', this.dialogDelete.form.id);
        this.$store.commit({
          type: 'showSnackbar',
          message: `データ実装が削除されました`
        });
      } catch (e) {
        console.error(e);
        this.$store.commit({
          type: 'showSnackbar',
          color: 'failed',
          message: `データ実装の削除に失敗しました`
        });
      }
    },
    createDataImplementation({dataComponentModelId, controllerId, updateType}) {
      this.showSnackbar({
        message: updateType === 'add' ? 'データ実装を作成中...' : 'データ実装を更新中...',
        timeout: false,
        loading: true
      });

      // データ実装作成
      return this._createDataImplementation({controllerId, dataComponentModelId})
          .then(() => {
            this.showSnackbar({
              message: updateType === 'add' ? 'データ実装を作成しました' : 'データ実装を更新しました'
            });
          }, () => {
            this.showSnackbar({
              color: 'failed',
              message: updateType === 'add' ? 'データ実装の作成に失敗しました' : 'データ実装の更新に失敗しました'
            });
          });
    }
  },
}
</script>
